import { getConfig } from '../config/env';

interface TokenResponse {
  access_token: string;
  token_type: string;
  expires_in?: number;
  scope?: string;
}

const SKEW_MS = 2 * 60 * 1000; // refresh 2 minutes before expiry

let cachedToken: {
  token: string;
  expiresAt: number;
  scope?: string;
} | null = null;

/**
 * Fetch a short-lived service token using client_credentials
 * Caches until near expiry and refreshes automatically.
 * 
 * Priority:
 * 1. OAuth client credentials (ROUTE_ROYAL_CLIENT_ID + ROUTE_ROYAL_CLIENT_SECRET)
 * 2. Static bearer token (ROUTE_ROYAL_BEARER_TOKEN) - DEV ONLY fallback
 * 
 * NOTE: OAuth is the production-grade approach. Static tokens are only for
 * emergency development scenarios when OAuth isn't available.
 */
export async function getServiceAuthHeader(): Promise<string | undefined> {
  const config = getConfig();
  const clientId = config.api.clientId || process.env.ROUTE_ROYAL_CLIENT_ID;
  const clientSecret =
    config.api.clientSecret || process.env.ROUTE_ROYAL_CLIENT_SECRET;
  
  // Build token URL - NOTE: /oauth/token is NOT under /api/v1 prefix in Route Royal
  // So we need to strip /api/v1 from the base URL if present
  let tokenUrl = process.env.ROUTE_ROYAL_TOKEN_URL;
  if (!tokenUrl) {
    // Remove /api/v1 suffix if present (oauth endpoint is excluded from global prefix)
    const baseUrl = config.api.routeRoyal.replace(/\/api\/v\d+\/?$/, '');
    tokenUrl = `${baseUrl}/oauth/token`;
  }
  
  const scope =
    process.env.ROUTE_ROYAL_CLIENT_SCOPE ||
    'sites:read properties:read projects:read organisations:read reference-data:read listings:read posts:read posts:categories:read posts:tags:read enquiries:write bookings:write contacts:write';

  if (!clientId || !clientSecret) {
    // Fallback: In development, allow static bearer token when OAuth credentials aren't set
    const isDevelopment = process.env.NODE_ENV === 'development';
    const staticBearerToken = process.env.ROUTE_ROYAL_BEARER_TOKEN;
    
    if (isDevelopment && staticBearerToken && staticBearerToken.length > 50) {
      cachedToken = {
        token: staticBearerToken,
        expiresAt: Date.now() + 24 * 60 * 60 * 1000,
      };
      return `Bearer ${staticBearerToken}`;
    }
    
    console.error(
      '[ServiceAuth] Missing client credentials. Set ROUTE_ROYAL_CLIENT_ID and ROUTE_ROYAL_CLIENT_SECRET.'
    );
    return undefined;
  }

  const now = Date.now();
  if (cachedToken && cachedToken.expiresAt - SKEW_MS > now) {
    // Validate cached token before using it
    if (!cachedToken.token || cachedToken.token.length < 50) {
      console.error('[ServiceAuth] Cached token appears invalid, fetching new token');
      cachedToken = null;
    } else {
      return `Bearer ${cachedToken.token}`;
    }
  }

  try {
    const resp = await fetch(tokenUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        grant_type: 'client_credentials',
        client_id: clientId,
        client_secret: clientSecret,
        scope,
      }),
    });

    // Accept both 200 OK and 201 Created as success
    if (!resp.ok && resp.status !== 201) {
      const body = await resp.json().catch(() => ({}));
      
      // Provide helpful error messages based on status code
      let errorMessage =
        body?.error_description ||
        body?.message ||
        `Service token request failed (${resp.status})`;

      if (resp.status === 401) {
        errorMessage +=
          ' — Invalid client credentials (ROUTE_ROYAL_CLIENT_ID / ROUTE_ROYAL_CLIENT_SECRET).';
      } else if (resp.status === 403) {
        errorMessage +=
          ' — Service client lacks required scopes.';
      } else if (resp.status === 404) {
        errorMessage +=
          ` — OAuth token endpoint not found at ${tokenUrl}.`;
      }

      console.error('[ServiceAuth] Token fetch failed:', errorMessage);
      throw new Error(errorMessage);
    }

    const responseText = await resp.text();

    let data: TokenResponse;
    try {
      data = JSON.parse(responseText) as TokenResponse;
    } catch {
      console.error('[ServiceAuth] Invalid JSON response from token endpoint');
      throw new Error('Invalid JSON response from token endpoint');
    }

    // Validate token response
    if (!data.access_token) {
      console.error('[ServiceAuth] Token response missing access_token');
      throw new Error('Invalid token response: access_token is missing');
    }

    // Validate token length (JWT should be at least 100+ characters)
    if (data.access_token.length < 50) {
      console.error('[ServiceAuth] Token appears truncated:', data.access_token.length, 'chars');
      throw new Error(
        `Token appears invalid or truncated (length: ${data.access_token.length})`
      );
    }

    const expiresInMs = (data.expires_in ?? 900) * 1000;
    cachedToken = {
      token: data.access_token,
      expiresAt: Date.now() + expiresInMs,
      ...(data.scope ? { scope: data.scope } : {}),
    };

    return `Bearer ${data.access_token}`;
  } catch (error) {
    if (error instanceof TypeError && error.message.includes('fetch')) {
      console.error(
        `[ServiceAuth] Network error connecting to OAuth endpoint at ${tokenUrl}`
      );
    }
    throw error;
  }
}
